import { useState } from 'react';
import styled from 'styled-components';
import { useRouter } from 'next/router';
import i18n from 'src/utils/translate';
import SortByOptions from 'src/modules/search/posts/components/sortByOptions';
import { titleSmall } from 'styles-js/mixins/typography';
import { LinkButton } from 'styles-js/buttons';
import { useUpdateSearchParam } from 'src/modules/search/hooks';
import Icon from 'src/components/icon';
const MAIN_OPTIONS_NUMBER = 3;
export type Option = {
  communityId: string;
  name: string;
  count: number;
};
type Props = {
  communityFilterOptions: Option[];
  isMember?: boolean;
};
export default function CommunityFilterOptions({
  communityFilterOptions = [],
  isMember
}: Props) {
  const router = useRouter();
  const [showMore, setShowMore] = useShowMoreCommunities((router.query.community as string), communityFilterOptions);
  const mainOptions = communityFilterOptions.slice(0, MAIN_OPTIONS_NUMBER);
  const moreOptions = communityFilterOptions.slice(MAIN_OPTIONS_NUMBER);
  return <>
      <OptionsContainer data-sentry-element="OptionsContainer" data-sentry-source-file="communityFilterOptions.tsx">
        <OptionsTitle data-sentry-element="OptionsTitle" data-sentry-source-file="communityFilterOptions.tsx">
          {i18n.t('Posted in')}
        </OptionsTitle>
        <div data-testid="search-sidebar-filter-options-community">
          <Option option={{
          communityId: 'all',
          name: i18n.t('All communities'),
          count: 0
        }} data-sentry-element="Option" data-sentry-source-file="communityFilterOptions.tsx" />
          {isMember && <Option option={{
          communityId: 'my',
          name: i18n.t('My communities'),
          count: 0
        }} />}
          {mainOptions.map(option => <Option key={option.communityId} option={option} />)}
          {showMore && moreOptions.map(option => <Option key={option.communityId} option={option} />)}
        </div>
        {communityFilterOptions.length > MAIN_OPTIONS_NUMBER && <LinkButton onClick={() => setShowMore(!showMore)}>
            {showMore ? <>
                <ToggleIcon icon="open-up" />
                <ToggleText>{i18n.t('View less')}</ToggleText>
              </> : <>
                <ToggleIcon icon="open-down" />
                <ToggleText>{i18n.t('View top 10 communities')}</ToggleText>
              </>}
          </LinkButton>}
      </OptionsContainer>
      <OptionsTitle data-sentry-element="OptionsTitle" data-sentry-source-file="communityFilterOptions.tsx">
        {i18n.t('Sort by')}
      </OptionsTitle>
      <SortByOptions data-sentry-element="SortByOptions" data-sentry-source-file="communityFilterOptions.tsx" />
    </>;
}
const Option = ({
  option
}: {
  option: Option;
}) => {
  const router = useRouter();
  const getLinkParams = useUpdateSearchParam('community');
  const {
    as,
    href
  } = getLinkParams(option.communityId);
  return <OptionLabel data-sentry-element="OptionLabel" data-sentry-component="Option" data-sentry-source-file="communityFilterOptions.tsx">
      <input checked={router.query.community === String(option.communityId) || !router.query.community && option.communityId === 'all'} id={option.communityId} type="radio" value={option.communityId} onChange={() => router.push(href, as, {
      scroll: false
    })} />
      <span>
        {option.name}
        {option.count > 0 && <CountLabel>
            {option.count}{' '}{option.count === 1 ? i18n.t('result') : i18n.t('results')}
          </CountLabel>}
      </span>
    </OptionLabel>;
};
function useShowMoreCommunities(selectedId: string, communityOptions: Option[]) {
  const selectedIndex = communityOptions.findIndex(({
    communityId
  }) => selectedId === String(communityId));
  const defaultShowMore = selectedIndex >= MAIN_OPTIONS_NUMBER;
  return useState(defaultShowMore);
}
const CountLabel = styled.span`
  padding-left: 8px;
  color: ${({
  theme
}) => theme.colorGreyDark};
`;
const OptionLabel = styled.label`
  display: block;
  margin-top: 5px;
  span {
    padding-left: 8px;
  }
`;
const ToggleIcon = styled(Icon)`
  font-size: 10px;
`;
const ToggleText = styled.span`
  padding-left: 12px;
`;
const OptionsContainer = styled.div`
  margin: 20px 0;
`;
const OptionsTitle = styled.div`
  ${titleSmall}
`;